export type UploadProgress = { loaded: number; total: number };

/**
 * The upload got no response at all: the connection dropped, or something on
 * the network (a proxy, a firewall) refused the storage host. A storage error
 * status is a refusal of this upload instead, and never raises this.
 */
export class UploadNetworkError extends Error {
  override readonly name = "UploadNetworkError";
}

/** Looks through wrapping errors, which carry the transport failure as `cause`. */
export const isUploadNetworkError = (error: unknown): boolean =>
  error instanceof UploadNetworkError ||
  (error instanceof Error && isUploadNetworkError(error.cause));

/**
 * PUTs a body to a presigned URL and reports how far it has got.
 *
 * `fetch` cannot do this: it exposes no upload progress event, and the
 * request-streaming alternative needs HTTP/2 plus duplex support that is not
 * dependable across browsers. XHR reports `loaded`/`total` on the way up and
 * gives cancellation the same way, which is what an upload that takes minutes
 * needs.
 *
 * Progress carries absolute bytes rather than a fraction, so a caller running
 * several uploads at once can sum them into one total.
 */
export function putWithProgress({
  url,
  body,
  contentType,
  label,
  onProgress,
  signal,
}: {
  url: string;
  body: Blob;
  contentType?: string;
  /** Named in error messages, usually the file name. */
  label?: string;
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}): Promise<void> {
  const subject = label ? `upload ${label}` : "upload file to storage";

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Upload aborted", "AbortError"));
      return;
    }

    const request = new XMLHttpRequest();
    request.open("PUT", url);
    if (contentType) {
      request.setRequestHeader("Content-Type", contentType);
    }

    const abort = () => request.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const cleanup = () => signal?.removeEventListener("abort", abort);

    // `lengthComputable` is false when the browser cannot size the body; the
    // caller then has no percentage to show and falls back to an indeterminate
    // state rather than rendering a bar stuck at zero.
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.({ loaded: event.loaded, total: event.total });
      }
    };

    request.onload = () => {
      cleanup();
      if (request.status >= 200 && request.status < 300) {
        // The last progress event can land short of the total, which would
        // leave a bar stuck just under full on an upload that has finished.
        onProgress?.({ loaded: body.size, total: body.size });
        resolve();
        return;
      }
      reject(new Error(`Failed to ${subject} (${request.status})`));
    };

    request.onerror = () => {
      cleanup();
      reject(new UploadNetworkError(`Failed to ${subject}`));
    };

    request.onabort = () => {
      cleanup();
      reject(new DOMException("Upload aborted", "AbortError"));
    };

    request.send(body);
  });
}
